import { requester } from '@/api/axios';

import { apiConst, keyConst } from '@/constants';

import {
  ApiResponse,
  CreateShareExpertBody,
  Expert,
  IEmailHistoryResponse,
  IRecordEmailPayload,
  ISendCurrentProjectNdaMailPayload,
  ISendCurrentProjectNdaResponse,
  PaginatedExpertResponse,
  ScreeningAnswerValue,
} from '@/interface';
import { IScreeningAnswersResponse } from '@/interface/expert.interface';

export interface CommentConversationResponse {
  conversation?: { id: string } | null;
  messages?: Record<string, unknown>[];
}

export interface ShareExpertLinkResponse {
  token: string;
  jwtToken: string;
}

export interface UnlockedEmailResult {
  apolloId: string;
  email: string | string[];
}

export const getExperts = async (page: number, limit: number): Promise<ApiResponse<PaginatedExpertResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getAllExperts}?page=${page}&limit=${limit}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export const markExpertNotificationsAsSeen = async (
  projectId: string,
  body: { projectExpertIds?: string[]; expertIds?: string[] },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.markExpertNotificationsSeen}/${projectId}`,
    data: body,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const markScreeningNotificationsAsSeen = async (
  projectId: string,
  body: { projectExpertIds?: string[] },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.markScreeningNotificationsSeen}/${projectId}`,
    data: body,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const markClientCommentNotificationsAsSeen = async (
  projectId: string,
  body: { projectExpertIds?: string[] },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.markClientCommentNotificationsSeen}/${projectId}`,
    data: body,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const getCommentConversationMessages = async (
  projectExpertId: string,
): Promise<ApiResponse<CommentConversationResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.commentConversation}/${projectExpertId}/comment-conversation`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const sendTenantCommentConversationMessage = async (
  conversationId: string,
  content: string,
  replyToMessageId?: string,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.commentConversationMessage}/${conversationId}/messages`,
    data: { content, replyToMessageId },
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const markTenantCommentConversationSeen = async (conversationId: string): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.commentConversationMessage}/${conversationId}/mark-seen`,
    data: {},
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const getPublicExpertStatusNotifications = async (projectId: string, jwtToken?: string) => {
  const query = jwtToken ? `?jwtToken=${encodeURIComponent(jwtToken)}` : '';
  const config = {
    method: 'get',
    url: `${apiConst.publicExpertStatusNotifications}/${projectId}${query}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

// export const getClientSubmittedExpertStatusNotifications = async (projectId: string) => {
//   const config = {
//     method: 'get',
//     url: `${apiConst.clientSubmittedExpertStatusNotifications}/${projectId}`,
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
//       'Content-Type': 'application/json',
//     },
//   };
//   return await requester(config);
// };
export const getPublicCommentConversationMessages = async (
  projectExpertId: string,
  jwtToken?: string,
): Promise<ApiResponse<CommentConversationResponse>> => {
  const query = jwtToken ? `?jwtToken=${encodeURIComponent(jwtToken)}` : '';
  const config = {
    method: 'get',
    url: `${apiConst.publicCommentConversation}/${projectExpertId}/comment-conversation${query}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const sendPublicCommentConversationMessage = async (
  conversationId: string,
  content: string,
  jwtToken?: string,
  replyToMessageId?: string,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.publicCommentConversationMessage}/${conversationId}/messages`,
    data: { content, jwtToken, replyToMessageId },
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const markPublicCommentConversationSeen = async (
  conversationId: string,
  jwtToken?: string,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.publicCommentConversationMessage}/${conversationId}/mark-seen`,
    data: { jwtToken },
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const markPublicExpertNotificationsAsSeen = async (
  projectId: string,
  body: { jwtToken?: string; projectExpertIds?: string[]; expertIds?: string[] },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.markPublicExpertNotificationsSeen}/${projectId}`,
    data: body,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const markClientSubmittedExpertNotificationsAsSeen = async (
  projectId: string,
  body: { projectExpertIds?: string[]; expertIds?: string[] },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: `${apiConst.markClientSubmittedExpertNotificationsSeen}/${projectId}`,
    data: body,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const getUnlockedEmail = async (ids: string[]): Promise<ApiResponse<UnlockedEmailResult[]>> => {
  const config = {
    method: 'post',
    url: apiConst.unlockedEmail,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: {
      ids,
    },
  };

  return await requester(config);
};
export const deleteExpert = async (id: string): Promise<ApiResponse<{ id: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.experts}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const gettSubmittedExperts = async (expertIds: string, jwtToken?: string): Promise<ApiResponse<Expert[]>> => {
  const query = jwtToken ? `?jwtToken=${encodeURIComponent(jwtToken)}` : '';
  const config = {
    method: 'get',
    url: `${apiConst.getSubmittedExperts}${expertIds}${query}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

// export const getClientSubmittedExperts = async (projectId: string): Promise<ApiResponse<Expert[]>> => {
//   const config = {
//     method: 'get',
//     url: `${apiConst.getClientSubmittedExperts}${projectId}/submitted-experts`,
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
//       'Content-Type': 'application/json',
//     },
//   };

//   return await requester(config);
// };

export const getClientSubmittedExperts = async (projectId: string): Promise<ApiResponse<Expert[]>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getSubmittedExperts}${projectId}/submitted-experts`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

interface UpdateExpertStatusBody {
  projectId: string;
  expertIds: string[];
  status?: string;
  jwtToken?: string;
}

export const updateExpertStatus = async (data: UpdateExpertStatusBody) => {
  const config = {
    method: 'patch',
    url: apiConst.expertupdateStatus,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data,
  };

  return await requester(config);
};
export const getExpertById = async (id: string, projectId?: string) => {
  const config = {
    method: 'get',
    url: `${apiConst.experts}/${id}${projectId ? `?projectId=${projectId}` : ''}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const createShareExpertLink = async (data: CreateShareExpertBody) => {
  const config = {
    method: 'post',
    url: apiConst.createShareExpertLink,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data,
  };

  return await requester(config);
};

export const getShareExpertLinkMeta = async (projectId: string): Promise<ApiResponse<ShareExpertLinkResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getShareExpertLinkMeta}/${projectId}/link`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const assignExperts = async (data: UpdateExpertStatusBody) => {
  const config = {
    method: 'post',
    url: apiConst.assignExperts,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data,
  };

  return await requester(config);
};

export const updateProjectExpertScreeningAnswers = async (
  projectExpertId: string,
  answers: Record<string, ScreeningAnswerValue>,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.projectExpertAnswers}/${projectExpertId}/screening-answers`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: { answers },
  };

  return await requester(config);
};

export const getProjectExpertScreeningAnswers = async (
  projectExpertId: string,
): Promise<ApiResponse<IScreeningAnswersResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.projectExpertAnswers}/${projectExpertId}/screening-answers`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export const updatePublicProjectExpertClientComment = async (
  projectExpertId: string,
  comment: string,
  jwtToken?: string,
): Promise<ApiResponse<{ clientComment?: string | null }>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.publicProjectExpertClientComment}/${projectExpertId}/client-comment`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: { comment, jwtToken },
  };

  return await requester(config);
};

export const updateClientProjectExpertClientComment = async (
  projectExpertId: string,
  comment: string,
): Promise<ApiResponse<{ clientComment?: string | null }>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.clientProjectExpertClientComment}/${projectExpertId}/client-comment`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: { comment },
  };

  return await requester(config);
};

export const createPublicScreeningLink = async (projectExpertId: string): Promise<ApiResponse<ShareExpertLinkResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.publicScreeningLink,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: { projectExpertId },
  };
  return await requester(config);
};

export const getPublicScreeningQuestions = async (projectExpertId: string, token: string): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'get',
    url: `${apiConst.publicScreeningQuestions}/${projectExpertId}/${token}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const translatePublicScreeningQuestions = async (
  projectExpertId: string,
  token: string,
  lang: string,
  includeUiStrings = false,
): Promise<ApiResponse<Record<string, unknown>>> => {
  const includeUiQuery = includeUiStrings ? '&includeUi=1' : '';
  const config = {
    method: 'get',
    url: `${apiConst.publicScreeningQuestions}/${projectExpertId}/${token}/translate?lang=${encodeURIComponent(lang)}${includeUiQuery}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const translatePublicStrings = async (
  projectExpertId: string,
  token: string,
  lang: string,
  strings: Record<string, string>,
): Promise<ApiResponse<Record<string, unknown>>> => {
  const config = {
    method: 'post',
    url: `${apiConst.publicScreeningQuestions}/${projectExpertId}/${token}/translate`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: { lang, strings },
  };

  return await requester(config);
};

export const submitPublicScreeningAnswers = async (
  projectExpertId: string,
  token: string,
  answers: Record<string, ScreeningAnswerValue>,
  acknowledgements?: {
    noticeAgreed?: boolean;
    termsAcknowledged?: boolean;
  },
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.publicScreeningQuestions}/${projectExpertId}/${token}`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: { answers, ...acknowledgements },
  };
  return await requester(config);
};

export const savePublicScreeningSignature = async (
  projectExpertId: string,
  token: string,
  signature: string,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.publicScreeningQuestions}/${projectExpertId}/${token}/signature`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: { signature },
  };
  return await requester(config);
};
export const publicApprovedStatus = async (data: UpdateExpertStatusBody) => {
  const config = {
    method: 'patch',
    url: apiConst.publicApprovedStatus,
    headers: {
      // Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data,
  };

  return await requester(config);
};

export const clientApprovedStatus = async (data: UpdateExpertStatusBody) => {
  const config = {
    method: 'patch',
    url: apiConst.clientApprovedStatus,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data,
  };

  return await requester(config);
};
// search Experts Request
export const searchExperts = async (data: Record<string, unknown>): Promise<ApiResponse<PaginatedExpertResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.searchExperts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};
export const searchCompanies = async (value: string) => {
  const config = {
    method: 'get',
    url: `${apiConst.experts}/company-search?query=${encodeURIComponent(value)}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const searchExpertTitles = async (value: string, companies?: string[]) => {
  const params = new URLSearchParams({ query: value });
  if (companies && companies.length > 0) {
    companies.forEach((c) => params.append('companies', c));
    // backend will receive ?companies=CompanyA&companies=CompanyB
  }
  const config = {
    method: 'get',
    url: `${apiConst.experts}/title-search?${params.toString()}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export const uploadSignedNda = async (
  file: Blob,
  filename: string,
  options?: { projectExpertId?: string; token?: string },
): Promise<ApiResponse<unknown>> => {
  const formData = new FormData();
  formData.append('file', file, `${filename}.pdf`);
  formData.append('filename', filename);
  if (options?.projectExpertId) formData.append('projectExpertId', options.projectExpertId);
  if (options?.token) formData.append('token', options.token);

  const config = {
    method: 'post',
    url: apiConst.uploadSignedNda,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  };
  return await requester(config);
};

export const getSignedNdaUrl = async (projectExpertId: string): Promise<ApiResponse<{ url: string }>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getSignedNdaUrl}/${projectExpertId}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const getLatestExpertSignedNdaUrl = async (
  expertId: string,
): Promise<ApiResponse<{ url: string; projectExpertId: string }>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getSignedNdaUrl}/expert/${expertId}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const getProjectExpertEmailHistory = async (projectExpertId: string): Promise<ApiResponse<IEmailHistoryResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.expertProjectEmail}/${projectExpertId}/email-history`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const recordProjectExpertEmail = async (
  projectExpertId: string,
  data: IRecordEmailPayload,
): Promise<ApiResponse<IEmailHistoryResponse>> => {
  const config = {
    method: 'post',
    url: `${apiConst.expertProjectEmail}/${projectExpertId}/record-email`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const sendCurrentProjectNda = async (
  projectExpertId: string,
  data: ISendCurrentProjectNdaMailPayload,
): Promise<ApiResponse<ISendCurrentProjectNdaResponse>> => {
  const config = {
    method: 'post',
    url: `${apiConst.expertProjectEmail}/${projectExpertId}/send-current-project-nda`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};
